import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { eswiUtils } from "@/tra/tancis/eswi/comn";
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import {
    BASE,
    URLS,
    APIS,
    SF_CSTM_OFCR_SEARCH_FORM,
    SF_CSTM_OFCR_EXCEL_FORM,
    SG_CSTM_OFCR_LIST,
    SG_CSTM_OFCR_EXCEL_FORM,
} from "@/tra/tancis/ptli/usr/cstmofcr/services/PtliCstmOfcrService";
import dayjs from "dayjs";

/*
 * Customs officer management list
 * !== 세관직원관리 목록 ==!
 */
export const PTLI0902001Q = (props: any) => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const [load, setLoad] = useState(false);

    useResource({
        defaultSchema: [{ area: "cstmCd" }, { area: "comnCd", comnCd: "PTL0001" }, { area: "cstmOrgzCd" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        cstmOfcrSrch: useForm({
            defaultSchema: SF_CSTM_OFCR_SEARCH_FORM,
            defaultValues: {
                ...(pgeStore?.form || {}),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        cstmOfcrSrchList: useGrid({
            defaultSchema: SG_CSTM_OFCR_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        cstmOfcrExcelList: useGrid({
            defaultSchema: SG_CSTM_OFCR_EXCEL_FORM,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get a list of Customs Officer. !== 세관직원 목록을 조회한다. ==!
        getCstmOfcrList: useFetch({
            api: (page = grid.cstmOfcrSrchList.page) =>
                APIS.getCstmOfcrList(form.cstmOfcrSrch.getValues(), page, grid.cstmOfcrSrchList.size),
            enabled: comnUtils.isEmpty(form.cstmOfcrSrch.errors),
            key: [grid.cstmOfcrSrchList.page, grid.cstmOfcrSrchList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.cstmOfcrSrch.getValues(),
                    page: grid.cstmOfcrSrchList.page,
                    size: grid.cstmOfcrSrchList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),
        // Get a excel list of Customs Officer. !== 세관직원 엑셀 목록을 조회한다. ==!
        getCstmOfcrExcelList: useFetch({
            api: (data: any) => (load ? APIS.getCstmOfcrExcelList(data) : null),
            enabled: comnUtils.isEmpty(form.cstmOfcrSrch.errors),
            onSuccess: () => {
                if (load) {
                    grid.cstmOfcrExcelList.exportExcel({
                        // Internal Portal Customs User Information !== 내부 포털_세관_사용자 정보 ==!
                        excelName: `${t("L_INT_PTL")}_${t("L_CSTM")}_${t("T_USR_INFO")}_${dayjs().format(
                            "DD/MM/YYYY HHmm",
                        )}`,
                        sheetName: "Sheet1",
                    });
                }
                setLoad(false);
            },
            onError: () => {},
            showToast: true,
        }),
    };

    /*
     * Declaration Event Render
     * !== 랜더 정의 ==!
     */
    const render = {
        grid_cstmOfcrSrchList: {
            cell: {
                usrDmnId: (props: any) => {
                    const { rowValues, value } = props;
                    return <Link to={`/ptli/usr/cstmofcr/ptli0902004q/${rowValues.usrDmnId}`}>{value}</Link>;
                },
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Search Button !== 검색버튼 ==!
        getCstmOfcrList: (page: number = 0) => {
            form.cstmOfcrSrch.handleSubmit(
                () => {
                    grid.cstmOfcrSrchList.setPage(page);
                    fetch.getCstmOfcrList.fetch(page);
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" });
                },
            )();
        },
        // Click Registration Button !== 등록 버튼 클릭 ==!
        onNewBtnClick: () => navigate(URLS.ptli0902002s),
        // Enter User name, user ID !== 사용자명, 사용자 ID 엔터 이벤트 ==!
        onEnterUsrInfo: () => {
            grid.cstmOfcrSrchList.setPage(0);
            fetch.getCstmOfcrList.fetch(0);
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.getCstmOfcrList();
    }, []);

    useEffect(() => {
        const store = getStore(pgeUid);
        if (!comnUtils.isEmpty(store) || load) {
            fetch.getCstmOfcrExcelList.fetch(store.form);
        }
    }, [load]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.cstmOfcrSrch.schema.usrDmnId} onEnter={handler.onEnterUsrInfo} />
                            <Group.Control {...form.cstmOfcrSrch.schema.usrNm} onEnter={handler.onEnterUsrInfo} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.cstmOfcrSrch.schema.cstmOfcrNo} onEnter={handler.onEnterUsrInfo} />
                            <Group.Control {...form.cstmOfcrSrch.schema.arrUsrStatCd} all={true} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.cstmOfcrSrch.schema.cstmSctnCd}
                                popupParams={{ cstmOrgzTpCd: "S" }}
                            />
                            <Group.Control {...form.cstmOfcrSrch.schema.cstmOfceCd}></Group.Control>
                        </Group.Row>

                        <Group.Row>
                            <Group.Control
                                {...form.cstmOfcrSrch.schema.cstmUnitCd}
                                popupParams={{ cstmOrgzTpCd: "U" }}
                            />
                        </Group.Row>
                    </Group.Section>

                    <Layout direction="row">
                        <Layout.Left>
                            <Button role={"reset"} onClick={() => form.cstmOfcrSrch.reset()} />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role="search"
                                onClick={() => {
                                    handler.getCstmOfcrList();
                                }}
                            ></Button>
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Layout direction="row">
                        <Layout.Left>
                            <Button
                                role="excelDown"
                                onClick={() => {
                                    setLoad(true);
                                }}
                            ></Button>
                        </Layout.Left>
                    </Layout>

                    <Group.Section>
                        <Grid
                            {...grid.cstmOfcrSrchList.grid}
                            data={fetch.getCstmOfcrList.data?.cstmOfcrList}
                            render={render.grid_cstmOfcrSrchList}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group hidden>
                <Grid {...grid.cstmOfcrExcelList.grid} data={fetch.getCstmOfcrExcelList.data?.cstmOfcrExcelList} />
            </Group>

            <Layout direction="row">
                <Layout.Left>
                    <Button role={"new"} onClick={handler.onNewBtnClick} />
                </Layout.Left>
            </Layout>
        </Page>
    );
};
